/**
 * storage.js -- IndexedDB persistence for ROMs, save states, and cheats.
 *
 * Three object stores:
 *
 *   roms        { id, name, size, addedAt, lastPlayedAt, lastSlot, syncedAt, data }
 *               `id` is the SHA-256 of the ROM bytes, so re-uploading the
 *               same cartridge updates the existing record instead of
 *               duplicating a few MB. `data` is an ArrayBuffer. `syncedAt`
 *               is the same dirty-flag seam `saves` uses (see below) --
 *               web/src/sync.js pushes a ROM to Supabase Storage once and
 *               marks it synced; ROMs are content-addressed and otherwise
 *               immutable, so there's no updatedAt-vs-syncedAt comparison
 *               needed the way saves' repeatedly-overwritten records need.
 *
 *   saves       { id, gameId, slot, createdAt, updatedAt, syncedAt, size, gameName, thumbnail }
 *               `id` is `${gameId}:${slot}`. METADATA ONLY -- deliberately no
 *               `data` field; see saveBlobs below.
 *
 *   saveBlobs   the raw libretro serialize blob (an ArrayBuffer) for one
 *               save, stored out-of-line under the SAME id as its `saves`
 *               record. Split out of `saves` in DB_VERSION 3 because
 *               IndexedDB reads a record as one indivisible unit: while the
 *               blob lived on the metadata record, anything that wanted only
 *               metadata (listSaves() for the save-state panel, a dirty check,
 *               a slot timestamp) still had to deserialise every
 *               multi-hundred-KB blob it touched. That cost scales with total
 *               library size and broke sync outright on a phone (see the
 *               "took longer than 10s" postmortems in CLAUDE.md). With the
 *               split, metadata reads never touch a blob at all, and a blob is
 *               read only when the actual save bytes are needed.
 *
 *   cheats      { id, gameId, code, description, enabled, createdAt, updatedAt, syncedAt }
 *               `id` is a random uuid (unlike roms/saves, there's no natural
 *               content-derived key -- two different cheats for the same
 *               game can otherwise look identical). No blob field, so unlike
 *               roms/saves there's no metadata-vs-full-record split needed;
 *               listCheats() returns whole records.
 *
 * The `syncedAt` field is the seam for the cloud-sync feature (web/src/sync.js):
 * a record is "dirty" once `syncedAt === null || syncedAt < updatedAt`. There
 * is deliberately NO network code in this file.
 */

const DB_NAME = 'snes9x-web';
const DB_VERSION = 3;

export const STORE_ROMS = 'roms';
export const STORE_SAVES = 'saves';
export const STORE_SAVE_BLOBS = 'saveBlobs';
export const STORE_CHEATS = 'cheats';

let dbPromise = null;

/**
 * Guard against a promise that never settles at all -- not a rejection, a
 * genuine hang with no error, no event, nothing. indexedDB.open() failing to
 * fire ANY of success/error/blocked, or a transaction never firing complete/
 * error/abort, are both real (if rare) browser-level hangs, not just
 * theoretical. Without this, a single stuck IndexedDB call would silently
 * wedge every future storage call in the page's lifetime too, since
 * openDatabase()'s result is cached in `dbPromise` below and every other
 * function awaits it first.
 */
function withDeadline(promise, ms, label) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label} took longer than ${ms / 1000}s to respond`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function openDatabase() {
  if (dbPromise) return dbPromise;

  const attempt = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = req.result;

      if (!db.objectStoreNames.contains(STORE_ROMS)) {
        const roms = db.createObjectStore(STORE_ROMS, { keyPath: 'id' });
        roms.createIndex('lastPlayedAt', 'lastPlayedAt');
        roms.createIndex('name', 'name');
      }

      if (!db.objectStoreNames.contains(STORE_SAVES)) {
        const saves = db.createObjectStore(STORE_SAVES, { keyPath: 'id' });
        // Lets the UI list every slot for one cartridge.
        saves.createIndex('gameId', 'gameId');
        // Pre-built for the future sync pass: find records never pushed, or
        // changed since the last push.
        saves.createIndex('syncedAt', 'syncedAt');
        saves.createIndex('updatedAt', 'updatedAt');
      }

      // DB_VERSION 3. Created EMPTY and nothing else: moving existing blobs
      // here is deliberately NOT done in this upgrade transaction. A library
      // of a few hundred MB would be exactly the kind of long single
      // operation that trips openDatabase()'s own 10s deadline, and failing
      // there is far worse than failing anywhere else -- the database can't
      // be opened at all. migrateSaveBlobs() moves them afterwards instead,
      // one record per transaction, in the background.
      if (!db.objectStoreNames.contains(STORE_SAVE_BLOBS)) {
        // Out-of-line keys: the value IS the ArrayBuffer, keyed by save id.
        db.createObjectStore(STORE_SAVE_BLOBS);
      }

      if (!db.objectStoreNames.contains(STORE_CHEATS)) {
        const cheats = db.createObjectStore(STORE_CHEATS, { keyPath: 'id' });
        cheats.createIndex('gameId', 'gameId');
        cheats.createIndex('syncedAt', 'syncedAt');
      }

      void event;
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () =>
      reject(new Error('IndexedDB upgrade blocked by another open tab -- close other tabs of this app and try again'));
  });

  // On a genuine hang (no callback ever fires), clear the cache so the NEXT
  // call gets a fresh open() attempt instead of permanently reusing this one.
  // If the original attempt eventually resolves anyway, after the deadline,
  // its result is just ignored -- a harmless wasted open.
  dbPromise = withDeadline(attempt, 10_000, 'Opening the local database').catch((err) => {
    dbPromise = null;
    throw err;
  });

  return dbPromise;
}

/**
 * `storeName` may be a single store name (fn receives that object store, the
 * common case) or an ARRAY of them, in which case fn receives the
 * transaction itself and picks stores off it with `.objectStore(name)`.
 *
 * The array form exists so a save's metadata and its blob can be written in
 * ONE transaction. That atomicity is load-bearing, not tidiness: IndexedDB
 * serialises overlapping readwrite transactions, so a concurrent
 * putSaveState() can never interleave between a migration's read and its
 * write, which is what would otherwise let a background migration resurrect
 * a stale blob over a save the user had just made.
 */
function tx(storeName, mode, fn) {
  const names = Array.isArray(storeName) ? storeName : [storeName];
  return openDatabase().then((db) =>
    withDeadline(
      new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, mode);
        const store = Array.isArray(storeName) ? transaction : transaction.objectStore(storeName);
        let result;
        try {
          result = fn(store);
        } catch (err) {
          transaction.abort();
          reject(err);
          return;
        }
        transaction.oncomplete = () => resolve(result && result.__req ? result.__req.result : result);
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
      }),
      10_000,
      `IndexedDB transaction on "${names.join('", "')}"`,
    ),
  );
}

/** Wrap an IDBRequest so `tx()` resolves with its result once the tx commits. */
function req(request) {
  return { __req: request };
}

/**
 * Disaster-recovery primitive: delete the ENTIRE local database -- every
 * ROM, save, and cheat -- so the next openDatabase() call recreates it from
 * scratch via the exact same onupgradeneeded path a brand-new browser
 * profile takes. Used by sync.js's forcePull() when local storage is
 * suspect (e.g. a hung/corrupted IndexedDB) but the cloud copy is trusted.
 * Deliberately more drastic than anything else in this file -- there is no
 * automatic path that calls this, only an explicit, separately-confirmed UI
 * action.
 */
export async function wipeLocalDatabase() {
  // If there's an existing live connection, close it first: deleteDatabase()
  // fires onblocked (not onsuccess/onerror) while ANY connection, including
  // this page's own, is still open. A short deadline here specifically --
  // if dbPromise is itself the thing that's stuck, don't let waiting on it
  // block the wipe that's meant to recover from exactly that.
  if (dbPromise) {
    try {
      const db = await withDeadline(dbPromise, 2_000, 'Closing the existing connection');
      db.close();
    } catch {
      // Already stuck or rejected -- nothing to close, proceed to delete anyway.
    }
  }
  dbPromise = null;

  // db.close() above doesn't take effect synchronously -- its "closing
  // steps" run as a separate task per spec -- so firing deleteDatabase()
  // immediately can race it and see the connection as still open. Retrying
  // a couple of times on "blocked" absorbs that race without a fixed,
  // possibly-too-short delay; it also gives a genuinely different blocker
  // (another tab that hasn't reacted to versionchange yet) a little room
  // before this gives up and reports it.
  let lastBlockedError = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 150));
    try {
      await withDeadline(
        new Promise((resolve, reject) => {
          const delReq = indexedDB.deleteDatabase(DB_NAME);
          delReq.onsuccess = () => resolve();
          delReq.onerror = () => reject(delReq.error);
          delReq.onblocked = () =>
            reject(new Error('Delete blocked -- close other tabs of this app and try again'));
        }),
        10_000,
        'Deleting the local database',
      );
      return;
    } catch (err) {
      if (!/^Delete blocked/.test(err.message)) throw err; // a real error, not a timing race -- surface it immediately
      lastBlockedError = err;
    }
  }
  throw lastBlockedError;
}

/**
 * Content hash of a ROM, used as its stable id.
 *
 * crypto.subtle is only exposed on secure origins. localhost counts as one,
 * and so does any https deployment, so the fallback below is for the odd case
 * of serving the dev build over plain http on a LAN IP. It is a cheap FNV-1a
 * over the bytes plus the length -- good enough to key a local cache, and it
 * is prefixed so the two id spaces can never collide.
 */
export async function hashRom(bytes) {
  if (globalThis.crypto?.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  let h = 0x811c9dc5;
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i++) {
    h ^= view[i];
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `fnv1a-${view.length.toString(16)}-${h.toString(16).padStart(8, '0')}`;
}

/* ---------------------------------------------------------------- ROMs -- */

/**
 * Store a ROM, or refresh the existing record if this cartridge is already
 * known. Returns the record (without re-reading the blob back out).
 */
export async function saveRom(name, bytes, meta = {}) {
  // Normalise to a standalone ArrayBuffer: a Uint8Array view over the wasm
  // heap would be both huge and unstable, and structured clone would copy
  // the whole heap with it.
  const buffer =
    bytes instanceof ArrayBuffer ? bytes.slice(0) : bytes.slice().buffer;

  const id = await hashRom(buffer);
  const existing = await getRom(id);
  const now = Date.now();

  const record = {
    id,
    name: existing?.name ?? name,
    size: buffer.byteLength,
    addedAt: meta.addedAt ?? existing?.addedAt ?? now,
    lastPlayedAt: now,
    // Which save-state slot was selected last time this game was played --
    // restored into the slot picker on load. Explicitly threaded through
    // from `existing` (rather than spread) like every other field here, so
    // re-uploading an already-known ROM doesn't silently wipe it back to null.
    lastSlot: existing?.lastSlot ?? null,
    // Content-addressed and otherwise immutable, so once synced it stays
    // synced across a re-upload of the same bytes -- unlike saves, there's
    // no "changed since" to re-flag. sync.js passes an explicit syncedAt
    // when writing a ROM it just downloaded from Supabase, same pattern as
    // putSaveState().
    syncedAt: meta.syncedAt !== undefined ? meta.syncedAt : (existing?.syncedAt ?? null),
    data: buffer,
  };

  await tx(STORE_ROMS, 'readwrite', (store) => store.put(record));
  return record;
}

export function getRom(id) {
  return tx(STORE_ROMS, 'readonly', (store) => req(store.get(id)));
}

/** Every ROM, most recently played first, WITHOUT the (large) `data` field. */
export async function listRoms() {
  const all = await tx(STORE_ROMS, 'readonly', (store) => req(store.getAll()));
  return (all ?? [])
    .map(({ data, ...meta }) => {
      void data;
      return meta;
    })
    .sort((a, b) => (b.lastPlayedAt ?? 0) - (a.lastPlayedAt ?? 0));
}

export async function touchRom(id) {
  const existing = await getRom(id);
  if (!existing) return null;
  existing.lastPlayedAt = Date.now();
  await tx(STORE_ROMS, 'readwrite', (store) => store.put(existing));
  return existing;
}

/** Mark a ROM as synced without touching its data -- used after a successful push. */
export async function markRomSynced(id, syncedAt) {
  const existing = await getRom(id);
  if (!existing) return null;
  existing.syncedAt = syncedAt;
  await tx(STORE_ROMS, 'readwrite', (store) => store.put(existing));
  return existing;
}

/**
 * ROMs never pushed to Supabase yet, WITH their `data` -- unlike listRoms(),
 * which strips it for the library UI, the sync push path needs the actual
 * bytes to upload.
 */
export async function listUnsyncedRoms() {
  const all = await tx(STORE_ROMS, 'readonly', (store) => req(store.getAll()));
  return (all ?? []).filter((r) => r.syncedAt == null);
}

/** Remember which save-state slot was last selected for this game. */
export async function setLastSlot(id, slot) {
  const existing = await getRom(id);
  if (!existing) return null;
  existing.lastSlot = slot;
  await tx(STORE_ROMS, 'readwrite', (store) => store.put(existing));
  return existing;
}

export async function deleteRom(id) {
  await tx(STORE_ROMS, 'readwrite', (store) => store.delete(id));
  // Drop the cartridge's save states and cheats with it, otherwise they are
  // orphaned and would silently count against the origin's storage quota
  // (or, for cheats, just clutter forever with no way to reach them).
  const saves = await listSaves(id);
  await Promise.all(saves.map((s) => deleteSave(s.id)));
  const cheats = await listCheats(id);
  await Promise.all(cheats.map((c) => deleteCheat(c.id)));
}

/* --------------------------------------------------------- save states -- */

export function saveStateId(gameId, slot) {
  return `${gameId}:${slot}`;
}

/**
 * Write a save state. Creates the record on first use of a slot and
 * overwrites it thereafter, preserving the original createdAt.
 *
 * `syncedAt` is reset to null on every write by default: the local copy has
 * moved on from whatever a server last saw, so a future sync layer must
 * treat it as dirty again. sync.js passes explicit `updatedAt`/`syncedAt`
 * (and sometimes `createdAt`) when writing a record it just pulled from
 * Supabase, so the local copy is immediately marked as already-synced
 * instead of being flagged dirty the moment it arrives.
 */
export async function putSaveState(gameId, slot, data, meta = {}) {
  const buffer =
    data instanceof ArrayBuffer ? data.slice(0) : data.slice().buffer;

  const id = saveStateId(gameId, slot);
  // Metadata only -- this just needs createdAt/gameName/thumbnail to carry
  // forward, and reading the outgoing blob to overwrite it would be pure waste.
  const existing = await getSaveStateMeta(gameId, slot);
  const now = Date.now();

  const record = {
    id,
    gameId,
    slot,
    createdAt: meta.createdAt ?? existing?.createdAt ?? now,
    updatedAt: meta.updatedAt ?? now,
    syncedAt: meta.syncedAt !== undefined ? meta.syncedAt : null,
    size: buffer.byteLength,
    gameName: meta.gameName ?? existing?.gameName ?? null,
    // A small PNG data URL, or null. Deliberately NOT stripped by listSaves()
    // the way `data` is -- it's small enough (a few/tens of KB) to include
    // in the metadata-only listing so the save-state list can show it
    // without loading each full multi-hundred-KB blob.
    thumbnail: meta.thumbnail !== undefined ? meta.thumbnail : (existing?.thumbnail ?? null),
  };

  // Metadata and blob in one transaction: a half-written save (a row the
  // list shows but whose bytes are missing, or an orphaned blob) is not a
  // state this should ever be able to leave behind.
  await tx([STORE_SAVES, STORE_SAVE_BLOBS], 'readwrite', (transaction) => {
    transaction.objectStore(STORE_SAVES).put(record);
    transaction.objectStore(STORE_SAVE_BLOBS).put(buffer, id);
  });
  return { ...record, data: buffer };
}

/** Mark a save state as synced without touching its data -- used after a successful push. */
export async function markSaveSynced(id, syncedAt) {
  const record = await tx(STORE_SAVES, 'readonly', (store) => req(store.get(id)));
  if (!record) return null;
  record.syncedAt = syncedAt;
  await tx(STORE_SAVES, 'readwrite', (store) => store.put(record));
  return record;
}

export function getSaveState(gameId, slot) {
  return getSaveStateById(saveStateId(gameId, slot));
}

/**
 * Metadata for one slot, WITHOUT `data` -- for a quick "does this slot have
 * anything, and when" check (the slot-change toast). Since DB_VERSION 3 this
 * is a genuine I/O saving and not just an API convenience: the blob lives in
 * a separate store, so this never reads it at all.
 *
 * (A not-yet-migrated record still carries its blob inline, so strip `data`
 * here for as long as migrateSaveBlobs() might not have reached it yet.)
 */
export async function getSaveStateMeta(gameId, slot) {
  const record = await tx(STORE_SAVES, 'readonly', (store) =>
    req(store.get(saveStateId(gameId, slot))),
  );
  if (!record) return null;
  const { data, ...meta } = record;
  void data;
  return meta;
}

/** All save states for a cartridge (metadata only), ordered by slot. */
export async function listSaves(gameId) {
  const all = await tx(STORE_SAVES, 'readonly', (store) =>
    req(store.index('gameId').getAll(gameId)),
  );
  return (all ?? [])
    .map(({ data, ...meta }) => {
      void data;
      return meta;
    })
    .sort((a, b) => a.slot - b.slot);
}

export async function deleteSave(id) {
  await tx([STORE_SAVES, STORE_SAVE_BLOBS], 'readwrite', (transaction) => {
    transaction.objectStore(STORE_SAVES).delete(id);
    // Unconditional: a not-yet-migrated record has no blob row and deleting a
    // missing key is a no-op, so this is correct for both shapes.
    transaction.objectStore(STORE_SAVE_BLOBS).delete(id);
  });
}

/**
 * Delete every save state for one game. Returns the deleted slot numbers
 * (not just a count), so a caller that also needs to clean up the
 * corresponding Supabase rows/Storage objects (see sync.js's
 * deleteAllRemoteSaves()) knows exactly which ones without a second query.
 */
export async function deleteAllSaves(gameId) {
  const saves = await listSaves(gameId);
  await Promise.all(saves.map((s) => deleteSave(s.id)));
  return saves.map((s) => s.slot);
}

/**
 * Read one of the `saves` indexes as a plain id -> index-key Map, WITHOUT
 * deserialising a single record.
 *
 * This is the whole point of the function: `getAll()` on this store reads
 * every matching record as one unit, multi-hundred-KB save blob included,
 * so using it just to compare two timestamp fields means deserialising the
 * entire local save library into memory. An index KEY cursor
 * (`openKeyCursor()`) hands back `cursor.key` (the indexed value) and
 * `cursor.primaryKey` (the record id) and never touches the record itself,
 * which is exactly the metadata a dirty-check needs and nothing more.
 *
 * Note IndexedDB deliberately omits records whose indexed field is
 * null/undefined from an index entirely -- so a never-synced record (whose
 * `syncedAt` is explicitly null, see putSaveState()) is simply absent from
 * the `syncedAt` map, which is precisely the "never pushed" signal callers
 * want. `updatedAt` is always set, so that map covers every record.
 */
function readSaveIndexMap(store, indexName, out) {
  const request = store.index(indexName).openKeyCursor();
  request.onsuccess = () => {
    const cursor = request.result;
    if (!cursor) return;
    out.set(cursor.primaryKey, cursor.key);
    cursor.continue();
  };
}

/** Every save state's id -> updatedAt, with no blob reads -- see readSaveIndexMap(). */
export async function listSaveUpdatedAtMap() {
  const updatedAt = new Map();
  await tx(STORE_SAVES, 'readonly', (store) => {
    readSaveIndexMap(store, 'updatedAt', updatedAt);
  });
  return updatedAt;
}

/**
 * The ids sync.js needs to push: never synced, or changed since the last
 * push. Returns ids only, NOT whole records -- the caller fetches each one
 * individually (getSaveStateById()) right before uploading it, so the blobs
 * of already-synced records are never read at all and only one dirty blob
 * is ever held in memory at a time.
 *
 * This shape is not cosmetic. Returning full records here meant a single
 * `getAll()` deserialising every save in the database at once, which a user
 * with a large local library hit as a hard sync failure on their phone
 * ("IndexedDB transaction on \"saves\" took longer than 10s to respond") --
 * the 10s transaction deadline doing its job on genuinely too-slow work,
 * not on a browser-level hang.
 */
export async function listUnsyncedSaveIds() {
  const syncedAt = new Map();
  const updatedAt = new Map();
  await tx(STORE_SAVES, 'readonly', (store) => {
    readSaveIndexMap(store, 'syncedAt', syncedAt);
    readSaveIndexMap(store, 'updatedAt', updatedAt);
  });

  const dirty = [];
  for (const [id, updated] of updatedAt) {
    const synced = syncedAt.get(id);
    if (synced == null || synced < updated) dirty.push(id);
  }
  return dirty;
}

/**
 * One full save record, blob included, by its storage id -- the only read
 * path that actually pulls the bytes, used when they're genuinely needed
 * (restoring a state, exporting, uploading to Supabase).
 *
 * Handles both shapes: a migrated record joins its blob from `saveBlobs`, a
 * not-yet-migrated one already carries `data` inline and is returned as-is.
 */
export async function getSaveStateById(id) {
  const out = await tx([STORE_SAVES, STORE_SAVE_BLOBS], 'readonly', (transaction) => {
    const result = {};
    const metaReq = transaction.objectStore(STORE_SAVES).get(id);
    metaReq.onsuccess = () => {
      const record = metaReq.result;
      if (!record) return;
      if (record.data) {
        result.record = record; // legacy, pre-migration
        return;
      }
      const blobReq = transaction.objectStore(STORE_SAVE_BLOBS).get(id);
      blobReq.onsuccess = () => {
        result.record = blobReq.result === undefined ? record : { ...record, data: blobReq.result };
      };
    };
    return result;
  });
  return out.record ?? null;
}

/**
 * Move every not-yet-split save blob out of its `saves` record and into
 * `saveBlobs`, one record per transaction. Safe to call on every boot and
 * safe to interrupt: whatever is left simply gets migrated next time.
 *
 * Deliberately NOT done inside onupgradeneeded -- see the note on the
 * STORE_SAVE_BLOBS creation above. Each record moves in its own small
 * readwrite transaction spanning both stores, so nothing here is ever a
 * single long operation that could trip a deadline, and a concurrent save or
 * sync stays correct throughout (readers handle both shapes; IndexedDB
 * serialises the overlapping readwrite transactions).
 */
export async function migrateSaveBlobs(onProgress) {
  // Cheap guard for the overwhelmingly common "nothing to do" boot: count()
  // reads no record bodies. Every migrated save has exactly one blob row, so
  // equal counts means the split is complete.
  const counts = await tx([STORE_SAVES, STORE_SAVE_BLOBS], 'readonly', (transaction) => {
    const result = {};
    const savesReq = transaction.objectStore(STORE_SAVES).count();
    savesReq.onsuccess = () => { result.saves = savesReq.result; };
    const blobsReq = transaction.objectStore(STORE_SAVE_BLOBS).count();
    blobsReq.onsuccess = () => { result.blobs = blobsReq.result; };
    return result;
  });
  if (counts.saves === counts.blobs) return { migrated: 0, alreadySplit: counts.saves };

  // Every already-migrated save has a blob row, so the difference is exactly
  // how many are still to move -- a real total to count progress against,
  // rather than the full record count (most of which may need no work).
  const total = counts.saves - counts.blobs;
  onProgress?.({ done: 0, total });

  const ids = await tx(STORE_SAVES, 'readonly', (store) => req(store.getAllKeys()));
  let migrated = 0;
  for (const id of ids ?? []) {
    if (await migrateOneSaveBlob(id)) {
      migrated++;
      onProgress?.({ done: migrated, total });
    }
  }
  return { migrated, alreadySplit: (counts.saves ?? 0) - migrated };
}

function migrateOneSaveBlob(id) {
  return tx([STORE_SAVES, STORE_SAVE_BLOBS], 'readwrite', (transaction) => {
    const result = { moved: false };
    const saves = transaction.objectStore(STORE_SAVES);
    const getReq = saves.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      // Gone, or already split by a previous pass/a newer write -- leave it.
      if (!record || !record.data) return;
      const { data, ...metaOnly } = record;
      transaction.objectStore(STORE_SAVE_BLOBS).put(data, id);
      saves.put(metaOnly);
      result.moved = true;
    };
    return result;
  }).then((result) => result.moved);
}

/* --------------------------------------------------------------- cheats -- */

function bytesToUuid(bytes) {
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

/**
 * Whether a value is syntactically a UUID -- the `cheats` table's `id`
 * column is `uuid` on the Supabase side (see supabase/schema.sql), so
 * anything else fails to push with "invalid input syntax for type uuid"
 * and, worse, throws and aborts the rest of that sync pass along with it.
 * Used both to guard the push path (sync.js) and to find/purge any local
 * cheat that was ever created with a non-UUID id before that guard existed.
 */
export function isValidUuid(value) {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function randomId() {
  if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
  // Fallback for a non-secure-context dev server (see hashRom()'s comment
  // above) -- still has to be a syntactically valid UUID, since it becomes
  // a Postgres `uuid` column's primary key the moment it's synced; Math.random()
  // isn't cryptographically unique but is plenty distinct for a client-only id.
  const bytes = Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  return bytesToUuid(bytes);
}

/**
 * A deterministic, syntactically-valid UUID derived from an arbitrary seed
 * string -- for a caller that wants the SAME logical key (e.g. "this
 * character's HP-fill cheat") to always resolve to the same storage id
 * without keeping a separate lookup table of its own. Not cryptographically
 * meaningful, just stable and collision-resistant enough for a small id
 * space. Same crypto.subtle-or-FNV-1a-fallback split as hashRom() above.
 */
export async function deterministicUuid(seed) {
  if (globalThis.crypto?.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(seed));
    return bytesToUuid(new Uint8Array(digest).slice(0, 16));
  }

  const bytes = new Uint8Array(16);
  for (let salt = 0; salt < 4; salt++) {
    let h = 0x811c9dc5 ^ salt;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    bytes[salt * 4] = h & 0xff;
    bytes[salt * 4 + 1] = (h >>> 8) & 0xff;
    bytes[salt * 4 + 2] = (h >>> 16) & 0xff;
    bytes[salt * 4 + 3] = (h >>> 24) & 0xff;
  }
  return bytesToUuid(bytes);
}

/**
 * Create or overwrite a cheat record by id. Local adds (addCheat()) generate
 * a fresh random id; sync.js's pull path instead calls this with the REMOTE
 * row's id, so a cheat added on another device gets the same id locally
 * (letting a later sync match it up and update it in place) rather than
 * being duplicated under a fresh one every pull.
 */
export async function putCheat(id, gameId, code, description, enabled, meta = {}) {
  const existing = await getCheat(id);
  const now = Date.now();
  const record = {
    id,
    gameId,
    code,
    description: description ?? '',
    enabled: Boolean(enabled),
    createdAt: meta.createdAt ?? existing?.createdAt ?? now,
    updatedAt: meta.updatedAt ?? now,
    syncedAt: meta.syncedAt !== undefined ? meta.syncedAt : null,
  };
  await tx(STORE_CHEATS, 'readwrite', (store) => store.put(record));
  return record;
}

/** Add one cheat code for a game. Starts disabled -- the user opts each one in explicitly. */
export function addCheat(gameId, code, description) {
  return putCheat(randomId(), gameId, code, description, false);
}

export function getCheat(id) {
  return tx(STORE_CHEATS, 'readonly', (store) => req(store.get(id)));
}

/** Patch a cheat record (toggling `enabled`, editing `code`/`description`, or both) by id. */
export async function updateCheat(id, patch, meta = {}) {
  const existing = await getCheat(id);
  if (!existing) return null;
  return putCheat(
    id,
    existing.gameId,
    patch.code ?? existing.code,
    patch.description ?? existing.description,
    patch.enabled ?? existing.enabled,
    meta,
  );
}

/** Mark a cheat as synced without touching its other fields -- used after a successful push. */
export async function markCheatSynced(id, syncedAt) {
  const existing = await getCheat(id);
  if (!existing) return null;
  existing.syncedAt = syncedAt;
  await tx(STORE_CHEATS, 'readwrite', (store) => store.put(existing));
  return existing;
}

export async function deleteCheat(id) {
  await tx(STORE_CHEATS, 'readwrite', (store) => store.delete(id));
}

/** Every cheat for one cartridge, in the order they were added. */
export async function listCheats(gameId) {
  const all = await tx(STORE_CHEATS, 'readonly', (store) =>
    req(store.index('gameId').getAll(gameId)),
  );
  return (all ?? []).sort((a, b) => a.createdAt - b.createdAt);
}

/** Cheats never pushed to Supabase yet, or changed locally since the last push. */
export async function listUnsyncedCheats() {
  const all = await tx(STORE_CHEATS, 'readonly', (store) => req(store.getAll()));
  return (all ?? []).filter((c) => c.syncedAt == null || c.syncedAt < c.updatedAt);
}

/* -------------------------------------------------------------- quota --- */

export async function estimateUsage() {
  if (!navigator.storage?.estimate) return null;
  try {
    return await navigator.storage.estimate();
  } catch {
    return null;
  }
}

/**
 * Ask the browser to make this origin's storage persistent, so the ROM cache
 * and save states survive automatic eviction under storage pressure. Chrome
 * grants this silently for engaged sites; Firefox prompts. A refusal is not
 * an error -- data still persists, it is just evictable.
 */
export async function requestPersistence() {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
